import { PUBLIC_SUPABASE_ANON_KEY, PUBLIC_SUPABASE_URL } from '$app/env/public'
import type {
  DatabaseWithJsonTypes,
  TypedSupabaseClient,
  View,
} from '$lib/supabase'
import type { PageDataSession, Settings } from '$lib/types'
import { getUrl } from '$lib/utils/general'
import { inMaintenanceMode } from '$lib/utils/maintenance'
import {
  clearLocalProfile,
  preloadedProfile,
} from '$lib/utils/session-profile-helpers.svelte'
import {
  clearLocalSettings,
  preloadedSettings,
} from '$lib/utils/session-settings-helpers.svelte'
import { isValidUsername } from '$lib/utils/username'
import {
  createBrowserClient,
  createServerClient,
  isBrowser,
} from '@supabase/ssr'
import type { Session, SupabaseClient } from '@supabase/supabase-js'
import { error, redirect } from '@sveltejs/kit'
import type { LayoutLoad, LayoutLoadEvent } from './$types'

let previousProfile: View<'profiles_with_stats'> | undefined = undefined
let previousSettings: Settings | undefined = undefined

const REALTIME_OPTIONS = { params: { eventsPerSecond: 200 } } as const

export const load: LayoutLoad = async (event) => {
  if (inMaintenanceMode() && event.url.pathname !== '/') {
    error(
      503,
      'Performing server maintenance. Please come back in a few minutes.',
    )
  }

  const { supabase, session } = await initSupabase(event)

  clearLocalProfile()
  clearLocalSettings()

  if (
    session &&
    (!isValidUsername(session.profile.username) ||
      !session.settings.accepted_tos_at) &&
    event.url &&
    !event.url.pathname.startsWith('/register/finish')
  ) {
    redirect(
      303,
      getUrl('/register/finish', { domain: false, redirect: event.url }),
    )
  }

  return {
    supabase,
    session,
  }
}

const loadProfile = async (
  supabaseClient: SupabaseClient<DatabaseWithJsonTypes>,
  session: Session,
): Promise<View<'profiles_with_stats'> | undefined> => {
  const { data: profileData, error: profileError } = await supabaseClient
    .from('profiles_with_stats')
    .select(`*`)
    .eq('id', session.user.id)
    .single()

  if (profileError) {
    console.error('Unable to load profile', profileError.message)
    return undefined
  }

  return profileData as View<'profiles_with_stats'>
}

const loadSettings = async (
  supabaseClient: SupabaseClient<DatabaseWithJsonTypes>,
  session: Session,
): Promise<Settings | undefined> => {
  const { data: settings, error: settingsError } = await supabaseClient
    .from('settings')
    .select(`*`)
    .eq('id', session.user.id)
    .single()

  if (settingsError) {
    console.error('Unable to load settings', settingsError.message)
    return undefined
  }

  return settings
}

const initSupabase = async ({ depends, fetch, data }: LayoutLoadEvent) => {
  let dataSession: PageDataSession | null = null

  depends('supabase:auth')

  const supabase = (isBrowser()
    ? createBrowserClient<DatabaseWithJsonTypes>(
        PUBLIC_SUPABASE_URL,
        PUBLIC_SUPABASE_ANON_KEY,
        {
          global: {
            fetch,
          },
          realtime: REALTIME_OPTIONS,
        },
      )
    : createServerClient<DatabaseWithJsonTypes>(
        PUBLIC_SUPABASE_URL,
        PUBLIC_SUPABASE_ANON_KEY,
        {
          global: {
            fetch,
          },
          realtime: REALTIME_OPTIONS,
          cookies: {
            getAll() {
              return data.cookies
            },
          },
        },
      )) as unknown as TypedSupabaseClient

  /**
   * It's fine to use `getSession` here, because on the client, `getSession` is
   * safe, and on the server, it reads `session` from the `LayoutData`, which
   * safely checked the session using `safeGetSession` (which validates the JWT
   * via `getUser`). Calling `getUser` again here would just add a redundant
   * auth network round-trip to every load/invalidation — and because the
   * browser client is a singleton, that call holds the auth lock long enough to
   * stall concurrent `getSession` calls (e.g. it made signing out take
   * seconds).
   */
  const {
    data: { session },
  } = await supabase.auth.getSession()

  // Now for some custom logic to add pausly specific session data to this
  // user.

  if (session) {
    let profile: View<'profiles_with_stats'> | undefined
    let settings: Settings | undefined

    if (preloadedProfile || preloadedSettings) {
      profile = preloadedProfile
      settings = preloadedSettings
      if (!profile) profile = previousProfile
      if (!settings) settings = previousSettings
    }

    if (!profile || !settings) {
      ;[profile, settings] = await Promise.all([
        loadProfile(supabase, session),
        loadSettings(supabase, session),
      ])
    }

    if (profile && settings) {
      dataSession = {
        ...session,
        profile,
        settings,
      }
      previousProfile = profile
      previousSettings = settings
    } else {
      supabase.auth.signOut()
      previousProfile = undefined
      previousSettings = undefined
    }
  }
  return { supabase, session: dataSession }
}
